import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { Breadcrumbs } from "@/components/breadcrumbs";
import { ProgrammesSection } from "@/components/landing/programmes-section";
import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { MobileCtaBar } from "@/components/landing/mobile-cta-bar";
import { programmes } from "@/content/programmes";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

const title = "Programmes: Kids English & IELTS Courses | Smart Word Labs";
const description =
  "Compare live online English programmes for kids 4-12 and private IELTS coaching for all ages: CEFR-aligned micro-cohorts and one-on-one prep.";

export const Route = createFileRoute("/programmes/")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/programmes` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/programmes` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Programmes", path: "/programmes" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Smart Word Labs English programmes",
          itemListElement: programmes.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.title,
            url: `${SITE_URL}/programmes/${p.slug}`,
          })),
        }),
      },
    ],
  }),
  component: ProgrammesIndex,
});

function ProgrammesIndex() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs trail={[{ name: "Programmes", path: "/programmes" }]} />

      <main className="pb-20 lg:pb-0">
        <section className="mx-auto max-w-6xl px-5 py-12">
          <h1 className="max-w-3xl text-4xl leading-tight text-primary sm:text-5xl">
            Online English programmes for children ages 4–12 &amp; IELTS candidates
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Age-banded curricula for children, taught live in micro-cohorts of no more than five and
            mapped to CEFR benchmarks, plus private one-on-one IELTS Academic &amp; General Training
            coaching for learners of any age. Pick the programme that matches your goal, or book a free
            assessment and we will place you.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-base font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
            >
              Book Free Assessment
            </Link>
            <Link
              to="/pricing"
              className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
            >
              See enrolment pricing
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>

        <ProgrammesSection />
      </main>

      <SiteFooter />
      <MobileCtaBar />
    </div>
  );
}
